import { action, observable, reaction, makeObservable } from 'mobx';
import {User, VersionedContentLookup, contentLookupService} from '../../../domain';
import {Disposers, HttpPromiseState, PromiseState} from '../../../lib/helpers';
import { ClassroomLoaders } from '../stores/classroom-loaders';
import { StudentDetailsResponse, ExaminationLessonItem } from '../models/classroom-models';
import { routes } from 'pages/Routes';
import { routerStore } from 'lib/components/router/routerStore';
import { environment } from 'lib/environment';
import Swal from 'sweetalert2';

export interface Lesson {
  id: number;
  title: string;
  date: Date;
}

export class TeacherStudentsSceneStore {
  
  @observable.ref
  resultState: HttpPromiseState<User[]> | null = null;

  @observable.ref
  lookupState: PromiseState<VersionedContentLookup> | null;
  
  @observable
  selectedStudent: User | null = null;
  
  // Homework Modal
  @observable
  isHomeworkModalOpen = false;
  
  @observable
  lessons: Lesson[] = [];
  
  @observable
  selectedLesson: Lesson | null = null;

  @observable
  searchQuery: string = '';

  // Student Details Modal
  @observable
  isStudentDetailsModalOpen = false;

  @observable
  studentDetails: StudentDetailsResponse | null = null;

  @observable
  isLoadingStudentDetails = false;

  // Exam Modal
  @observable
  isExamModalOpen = false;

  @observable
  examinationLessons: ExaminationLessonItem[] = [];

  @observable
  selectedExaminationLesson: ExaminationLessonItem | null = null;

  @observable
  isLoadingExaminationLessons = false;

  @observable
  examTimeLimitMinutes: number | null = null;

  @observable
  selectedExamIdForResult: number | null = null;

  private readonly disposers = new Disposers();

  constructor() {
      makeObservable(this);      
      this.resultState = null;
      this.lookupState = contentLookupService.load();
      this.setup();
    }  

  loadResults = () => {
    if (this.resultState?.isPending === true)
      return;
      
    let url = `${environment.apiServer}/teacher/students`;

    if (this.searchQuery) {
      url += `?search=${encodeURIComponent(this.searchQuery)}`;
    }
    
    this.resultState = ClassroomLoaders.Teacher.getTeacherStudents(url);
  };

  private setup() {
    this.loadResults();

    this.disposers.add(
      reaction(
        () => this.searchQuery,
        (query) => {
          if (query.length >= 2 || query.length === 0) {
            this.loadResults();
          }
        },
        { delay: 500 }
      )
    );
  } 

  @action
  loadLookup = () => {
    if (this.lookupState?.isPending === true)
      return;
    this.lookupState = contentLookupService.load();
  };

  // Homework Modal Methods
  @action
  openHomeworkModal = (student: User) => {
    this.selectedStudent = student;
    this.isHomeworkModalOpen = true;
    this.loadLessons(student);
  };

  @action
  closeHomeworkModal = () => {
    this.isHomeworkModalOpen = false;
    this.selectedStudent = null;
    this.selectedLesson = null;
    this.lessons = [];
  };

  @action
  setSelectedLesson = (lesson: Lesson) => {
    this.selectedLesson = lesson;
  };

  @action
  sendHomework = () => {
    if (!this.selectedLesson) {
      return;
    }
    
    const url = routes.teacherClassroom(this.selectedLesson.id);
    routerStore.navigateTo(url + "?homework");
    
    this.closeHomeworkModal();
  };

  private loadLessons = (student: User) => {
    const mockLessons: Lesson[] = [];

    this.lessons = [];
    ClassroomLoaders.Teacher.getStudentLessons(student.Id).then(response => {
        response.data.map(room => {
          mockLessons.push({id: room.Id, title: room.Lesson?.Name ?? "", date: new Date(room.ScheduledStartTime * 1000)});
          this.lessons = mockLessons;
      })
    });    
  };

  @action
  setSearchQuery = (query: string) => {
    this.searchQuery = query;
  };

  // Student Details Modal Methods
  @action
  openStudentDetailsModal = async (student: User) => {
    this.selectedStudent = student;
    this.isStudentDetailsModalOpen = true;
    this.isLoadingStudentDetails = true;
    this.studentDetails = null;

    try {
      const response = await ClassroomLoaders.Teacher.getStudentDetails(student.Id);
      this.studentDetails = response.data;
    } catch (error) {
      console.error('Failed to load student details:', error);
      Swal.fire({ icon: 'error', text: 'Failed to load student details' });
    } finally {
      this.isLoadingStudentDetails = false;
    }
  };

  @action
  closeStudentDetailsModal = () => {
    this.isStudentDetailsModalOpen = false;
    this.studentDetails = null;
    this.isLoadingStudentDetails = false;
  };

  // Exam Modal Methods
  @action
  openExamModal = async (student: User) => {
    this.selectedStudent = student;
    this.isExamModalOpen = true;
    this.isLoadingExaminationLessons = true;
    this.examinationLessons = [];
    this.selectedExaminationLesson = null;
    this.examTimeLimitMinutes = null;

    try {
      const response = await ClassroomLoaders.Teacher.getExaminationLessons(student.Id);
      this.examinationLessons = response.data;
    } catch (error) {
      console.error('Failed to load examination lessons:', error);
      const err = error as any;
      const message = err?.response?.data?.Message || 'Failed to load examination lessons';
      Swal.fire({ icon: 'error', text: message });
    } finally {
      this.isLoadingExaminationLessons = false;
    }
  };

  @action
  closeExamModal = () => {
    this.isExamModalOpen = false;
    this.selectedStudent = null;
    this.examinationLessons = [];
    this.selectedExaminationLesson = null;
    this.isLoadingExaminationLessons = false;
    this.examTimeLimitMinutes = null;
  };

  @action
  setSelectedExaminationLesson = (lesson: ExaminationLessonItem) => {
    this.selectedExaminationLesson = lesson;
  };

  @action
  setExamTimeLimitMinutes = (value: number | null) => {
    this.examTimeLimitMinutes = value;
  };

  @action
  setSelectedExamIdForResult = (examId: number | null) => {
    this.selectedExamIdForResult = examId;
  };

  @action
  sendExam = async () => {
    if (!this.selectedExaminationLesson || !this.selectedStudent) {
      return;
    }

    try {
      await ClassroomLoaders.Teacher.createExam({
        StudentId: this.selectedStudent.Id,
        LessonId: this.selectedExaminationLesson.LessonId,
        TimeLimitMinutes: this.examTimeLimitMinutes
      });
      
      await Swal.fire({ icon: 'success', text: 'Exam sent successfully!' });
      this.closeExamModal();
    } catch (error) {
      console.error('Failed to create exam:', error);
      const err = error as any;
      const message = err?.response?.data?.Message || 'Failed to send exam';
      Swal.fire({ icon: 'error', text: message });
    }
  };

  dispose = () => {
    this.disposers.dispose();
  };
}
